import ViewportMgr from 'coreClasses/view/ViewportMgr';
import {GRID_CONFIG} from 'brandConstants';
import AbstractSearchProvider from 'coreClasses/search/providers/AbstractSearchProvider';
import {isServer} from 'coreUtils/utils';

type GridConfig = {
	availableValues: number[];
	default: number;
}

const DEFAULT_GRID_MODE = 'MOBILE';

/**
 * Represents GridModel
 *
 * TODO: superseded by the `useGridSize` hook; its doc block holds the removal plan.
 */
class GridModel {
	private searchProvider: AbstractSearchProvider;
	private gridSize: number;
	private gridSizeKeyPrefix: string = 'grid-size';

	/**
	 * @constructor
	 * @param {AbstractSearchProvider} searchProvider - Search Provider
	 */
	constructor(searchProvider: AbstractSearchProvider) {
		this.searchProvider = searchProvider;

		this.gridSize = this.getPreferredGridSize();
	}

	/**
	 * Returns the gridSizeKey based on the device viewport
	 * @returns {String}
	 */
	get gridSizeKey(): string {
		const postFix = ViewportMgr.isMobile() ? 'mob' : 'dsk';

		return `${this.gridSizeKeyPrefix}-${postFix}`;
	}

	/**
	 * Resets the gridSize to the most accurate value available
	 */
	public resetGridSize(): void {
		this.updateGridSize(this.getPreferredGridSize());
	}

	/**
	 * Updates grid size
	 * @param {number} gridSize
	 * @returns {void}
	 */
	public updateGridSize(gridSize: number): void {
		if (this.gridSize !== gridSize) {
			this.gridSize = gridSize;
			this.updatePreferredGridSize();
			this.searchProvider.updateGridSize(this.getPreferredGridSize());
		}
	}

	/**
	 * Returns the current gridSize
	 * @returns {number}
	 */
	public getGridSize(): number {
		return this.gridSize;
	}

	/**
	 * Returns the grid configuration based on the device viewport
	 * @param {string} device - device viewport
	 * @returns {GridConfig}
	 */
	public static getGridConfig(device: string = DEFAULT_GRID_MODE): GridConfig {
		let gridConfigDevice = {};

		if (GRID_CONFIG.hasOwnProperty(device)) {
			gridConfigDevice = GRID_CONFIG[device];
		}

		return Object.assign({}, GRID_CONFIG[DEFAULT_GRID_MODE], gridConfigDevice);
	}

	/**
	 * Stores the preferred gridSize in the localStorage
	 * @protected
	 * @returns {void}
	 */
	private updatePreferredGridSize() : void {
		return typeof localStorage !== 'undefined'
			? localStorage.setItem(this.gridSizeKey, this.gridSize.toString())
			: undefined;
	}

	/**
	 * Retrieves the preferred gridSize from the localStorage
	 * @returns {Number}
	 * @private
	 */
	private getPreferredGridSize(): number {
		let preferredGridSize = typeof localStorage !== 'undefined'
			? parseInt(localStorage.getItem(this.gridSizeKey))
			: null;

		if (!preferredGridSize && !isServer) {
			preferredGridSize = GridModel.getGridConfig(ViewportMgr.isMobile() ? 'MOBILE' : 'DESKTOP').default;
		}

		return preferredGridSize;
	}
}

export default GridModel;
